import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { useLazyDownloadCsvEnrichmentQuery } from "@/pages/Private/redux/csvEnrichment/csvEnrichment.api";
import { runOrToast } from "@/utils/runOrToast";

import { CsvEnrichment } from "../../../CsvEnrichment/schema/csvEnrichment";

/** Hand a downloaded blob to the browser under `name`, with exactly one `.xlsx` extension. */
function saveBlobAsXlsx(blob: Blob, name: string): void {
	const url = window.URL.createObjectURL(blob);
	const anchor = document.createElement("a");

	anchor.href = url;
	anchor.download = `${name.replaceAll(".xlsx", "")}.xlsx`;
	document.body.appendChild(anchor);
	anchor.click();
	window.URL.revokeObjectURL(url);
	document.body.removeChild(anchor);
}

/**
 * The download button of an uploaded file on the list source step. A failed download used to
 * escape as an unhandled rejection and left its spinner turning for good (PREC-4390); it now says
 * why, and the spinner stops either way.
 */
export const useSourceFileDownload = () => {
	const { t } = useTranslation();
	const [downloadCsvEnrichment] = useLazyDownloadCsvEnrichmentQuery();
	const [downloadIsLoading, setDownloadIsLoading] = useState<number | null>(null);

	const handleDownload = useCallback(
		async (csvEnrichment: CsvEnrichment) => {
			setDownloadIsLoading(csvEnrichment.id);
			await runOrToast(async () => {
				const blob = await downloadCsvEnrichment(csvEnrichment.id).unwrap();

				saveBlobAsXlsx(blob, csvEnrichment.name);
			}, t("errors.downloadFailed"));
			setDownloadIsLoading(null);
		},
		[downloadCsvEnrichment, t]
	);

	return { downloadIsLoading, handleDownload };
};
